import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ResumeSnapshotCard from '../components/hr-career-tools/ResumeSnapshotCard';
import JobMatcherCard from '../components/hr-career-tools/JobMatcherCard';
import UpskillRecommenderCard from '../components/hr-career-tools/UpskillRecommenderCard';
import InterviewGeneratorCard from '../components/hr-career-tools/InterviewGeneratorCard';

// Component for the HR/Career Tools page
const HRCareerToolsPage = (): React.ReactElement => {
  const navigate = useNavigate();
  // Define tab types to avoid comparison errors
  type TabType = 'resume-snapshot' | 'job-matcher' | 'upskill' | 'interview';
  const [activeTab, setActiveTab] = useState<TabType>('resume-snapshot');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [jobDescFile, setJobDescFile] = useState<File | null>(null);
  const [upskillFile, setUpskillFile] = useState<File | null>(null);
  const [jobDescription, setJobDescription] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  
  const resumeFileInputRef = useRef<HTMLInputElement>(null);
  const jobDescFileInputRef = useRef<HTMLInputElement>(null);
  const upskillFileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setFile: React.Dispatch<React.SetStateAction<File | null>>) => {
    setFileError('');
    const file = e.target.files?.[0];
    
    if (!file) return;
    
    // Check file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setFileError('Please upload only PDF, DOC, or DOCX files');
      e.target.value = '';
      return;
    }
    
    setFile(file);
  };
  
  // State for API response data and loading state
  const [resumeData, setResumeData] = useState<any>(null);
  const [jobMatchData, setJobMatchData] = useState<any>(null);
  const [upskillData, setUpskillData] = useState<any>(null);
  const [interviewData, setInterviewData] = useState<any>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<{[key: string]: string}>({resume: '', job: '', upskill: '', interview: ''});
  const [processingDone, setProcessingDone] = useState<{[key: string]: boolean}>({resume: false, job: false, upskill: false, interview: false});

  const clearForm = (fileType: string) => {
    if (fileType === 'resume') {
      setResumeFile(null);
      setResumeData(null);
      if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
      setProcessingDone({...processingDone, resume: false});
    } else if (fileType === 'job') {
      setJobDescFile(null);
      setJobDescription('');
      setJobMatchData(null);
      if (jobDescFileInputRef.current) jobDescFileInputRef.current.value = '';
      setProcessingDone({...processingDone, job: false});
    } else if (fileType === 'upskill') {
      setUpskillFile(null);
      setUpskillData(null);
      if (upskillFileInputRef.current) upskillFileInputRef.current.value = '';
      setProcessingDone({...processingDone, upskill: false});
    } else if (fileType === 'interview') {
      setResumeFile(null);
      setInterviewData(null);
      if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
      setProcessingDone({...processingDone, interview: false});
    }
    setFileError('');
    setUploadError(null);
  };
  
  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess({...copySuccess, [type]: 'Copied!'});
      setTimeout(() => setCopySuccess({...copySuccess, [type]: ''}), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setCopySuccess({...copySuccess, [type]: 'Failed to copy'});
      setTimeout(() => setCopySuccess({...copySuccess, [type]: ''}), 2000);
    }
  };

  // Function to render the appropriate tool content based on the active tab
  const renderToolContent = () => {
    switch (activeTab) {
      case 'resume-snapshot':
        return (
          <ResumeSnapshotCard
            resumeFile={resumeFile}
            setResumeFile={setResumeFile}
            resumeData={resumeData}
            setResumeData={setResumeData}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
            fileError={fileError}
            setFileError={setFileError}
            copySuccess={copySuccess}
            setCopySuccess={setCopySuccess}
            processingDone={processingDone}
            setProcessingDone={setProcessingDone}
            handleFileChange={handleFileChange}
            copyToClipboard={copyToClipboard}
            clearForm={clearForm}
          />
        );
      case 'job-matcher':
        return (
          <JobMatcherCard
            jobDescFile={jobDescFile}
            setJobDescFile={setJobDescFile}
            jobDescription={jobDescription}
            setJobDescription={setJobDescription}
            jobMatchData={jobMatchData}
            setJobMatchData={setJobMatchData}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
            fileError={fileError}
            setFileError={setFileError}
            copySuccess={copySuccess}
            setCopySuccess={setCopySuccess}
            processingDone={processingDone}
            setProcessingDone={setProcessingDone}
            handleFileChange={handleFileChange}
            copyToClipboard={copyToClipboard}
            clearForm={clearForm}
          />
        );
      case 'upskill':
        return (
          <UpskillRecommenderCard
            upskillFile={upskillFile}
            setUpskillFile={setUpskillFile}
            upskillData={upskillData}
            setUpskillData={setUpskillData}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
            fileError={fileError}
            setFileError={setFileError}
            copySuccess={copySuccess}
            setCopySuccess={setCopySuccess}
            processingDone={processingDone}
            setProcessingDone={setProcessingDone}
            handleFileChange={handleFileChange}
            copyToClipboard={copyToClipboard}
            clearForm={clearForm}
          />
        );
      case 'interview':
        return (
          <InterviewGeneratorCard
            resumeFile={resumeFile}
            setResumeFile={setResumeFile}
            interviewData={interviewData}
            setInterviewData={setInterviewData}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
            fileError={fileError}
            setFileError={setFileError}
            copySuccess={copySuccess}
            setCopySuccess={setCopySuccess}
            processingDone={processingDone}
            setProcessingDone={setProcessingDone}
            handleFileChange={handleFileChange}
            copyToClipboard={copyToClipboard}
            clearForm={clearForm}
          />
        );
      default:
        return <div>Select a tool to get started</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h1 className="text-3xl font-bold">HR/Career Tools</h1>
            </div>
            <p className="text-blue-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Tools for HR professionals and job seekers to optimize the hiring process
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Tool Selection Tabs */}
          <div className="flex mb-6 border-b dark:border-gray-700">
            <button
              className={`px-6 py-3 font-medium rounded-t-lg flex flex-col items-center ${activeTab === 'resume-snapshot' 
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30'}`}
              onClick={() => setActiveTab('resume-snapshot')}
            >
              <span className="text-lg font-bold">Resume Snapshot</span>
              <span className={`text-xs mt-1 ${activeTab === 'resume-snapshot' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                Instantly parse resume text into a clean, structured profile
              </span>
            </button>
            <button
              className={`px-6 py-3 font-medium rounded-t-lg flex flex-col items-center ${activeTab === 'job-matcher' 
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/30'}`}
              onClick={() => setActiveTab('job-matcher')}
            >
              <span className="text-lg font-bold">Job Matcher</span>
              <span className={`text-xs mt-1 ${activeTab === 'job-matcher' ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}`}>
                Match resumes to job descriptions for better hiring decisions
              </span>
            </button>
            <button
              className={`px-6 py-3 font-medium rounded-t-lg flex flex-col items-center ${activeTab === 'upskill' 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30'}`}
              onClick={() => setActiveTab('upskill')}
            >
              <span className="text-lg font-bold">Upskill Recommender</span>
              <span className={`text-xs mt-1 ${activeTab === 'upskill' ? 'text-purple-100' : 'text-gray-500 dark:text-gray-400'}`}>
                Get personalized skill development recommendations
              </span>
            </button>
            <button
              className={`px-6 py-3 font-medium rounded-t-lg flex flex-col items-center ${activeTab === 'interview' 
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/30'}`}
              onClick={() => setActiveTab('interview')}
            >
              <span className="text-lg font-bold">Interview Generator</span>
              <span className={`text-xs mt-1 ${activeTab === 'interview' ? 'text-orange-100' : 'text-gray-500 dark:text-gray-400'}`}>
                Generate a set of interview questions based from the resume
              </span>
            </button>
          </div>
          
          {/* Tool Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            {renderToolContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRCareerToolsPage;
